import React, { useState, useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import Navbar from "../components/Navbar";
import "../css/Prodotti.css";
import "../css/global.css";
import Slideshow from "../components/Slideshow";
import InfoBox from "../components/InfoBox";
import API from "../config/api";
// Fallback to static categories if API fails
import staticCategories from "../data/categories";

// Icon paths from public/assets
const windowIcon = "/assets/icons/window.png";
const doorIcon = "/assets/icons/door.png";
const blindsIcon = "/assets/icons/blinds.png";
const gateIcon = "/assets/icons/gate.png";

const Prodotti = () => {
  const location = useLocation();
  const [activeCategory, setActiveCategory] = useState("finestre"); // Set finestre as default
  const [categoryImages, setCategoryImages] = useState([]);
  const [imagesLoading, setImagesLoading] = useState(true);
  const [categories, setCategories] = useState(staticCategories);
  const [categoriesLoading, setCategoriesLoading] = useState(true);
  const detailsRef = useRef(null);

  // Fetch categories from API on mount
  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const response = await fetch(API.ENDPOINTS.CATEGORIES);
        const data = await response.json();
        // Validate that data contains expected category structure
        if (data && Object.keys(data).length > 0 && data.finestre) {
          setCategories(data);
        }
      } catch (error) {
        console.error("Error fetching categories:", error);
        // Keep static categories as fallback
      } finally {
        setCategoriesLoading(false);
      }
    };

    fetchCategories();
  }, []);

  // Fetch images from PHP backend when category changes or categories finish loading
  useEffect(() => {
    const fetchImages = async () => {
      setImagesLoading(true);
      try {
        const response = await fetch(API.ENDPOINTS.IMAGES(activeCategory));
        const data = await response.json();
        if (data.success && data.images) {
          // Prepend the assets URL to get full image paths from backend
          const fullPaths = data.images.map((img) => `${API.ASSETS_URL}${img}`);
          setCategoryImages(fullPaths);
        } else {
          setCategoryImages([]);
        }
      } catch (error) {
        // Silently handle fetch errors - images will show loading state
        setCategoryImages([]);
      }
      setImagesLoading(false);
    };

    // Only fetch images after categories have loaded
    if (activeCategory && !categoriesLoading) {
      fetchImages();
    }
  }, [activeCategory, categoriesLoading]);

  useEffect(() => {
    window.scrollTo(0, 0);
    const params = new URLSearchParams(location.search);
    const categoryParam = params.get("category");
    if (categoryParam && categories[categoryParam]) {
      setActiveCategory(categoryParam);
    }
  }, [location, categories]);

  useEffect(() => {
    // Scroll animation observer
    const observerOptions = {
      threshold: 0.1,
      rootMargin: "0px 0px -100px 0px",
    };

    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.classList.add("animate-in");
        }
      });
    }, observerOptions);

    const animateElements = document.querySelectorAll(".section-animate");
    animateElements.forEach((el) => observer.observe(el));

    // Ensure details section is visible immediately once loaded
    if (detailsRef.current) {
      detailsRef.current.classList.add("animate-in");
    }

    return () => observer.disconnect();
  }, [activeCategory, categoriesLoading, imagesLoading]);

  const currentCategory = activeCategory ? categories[activeCategory] : null;

  const getCategoryIcon = (key) => {
    const icons = {
      finestre: windowIcon,
      porte: doorIcon,
      tende: blindsIcon,
      altro: gateIcon,
    };
    return icons[key] || windowIcon;
  };

  if (categoriesLoading) {
    return (
      <div className="prodotti-page">
        <Navbar />
        <div className="loading-container">
          <p>Caricamento...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="prodotti-page">
      <Navbar />

      {/* Compact Hero Section */}
      <section className="prodotti-hero-compact">
        <div className="prodotti-hero-overlay"></div>
        <div className="prodotti-hero-content-compact">
          <span className="section-subtitle">LA NOSTRA GAMMA</span>
          <h1 className="prodotti-hero-title-compact">I Nostri Prodotti</h1>
          <p className="prodotti-hero-slogan-compact">
            FER-AL realizza su misura una vasta gamma di prodotti per soddisfare
            ogni Vostra esigenza
          </p>
        </div>
      </section>

      {/* Category Navbar */}
      <section className="category-navbar-section">
        <div className="category-navbar-container">
          {Object.keys(categories).map((key) => (
            <button
              key={key}
              className={`category-nav-item ${
                activeCategory === key ? "active" : ""
              }`}
              onClick={() => setActiveCategory(key)}
            >
              <img
                src={getCategoryIcon(key)}
                alt={categories[key].title}
                className="category-nav-icon"
              />
              <span className="category-nav-title">
                {categories[key].title}
              </span>
            </button>
          ))}
        </div>
      </section>

      {/* Category Details Section */}
      {currentCategory && (
        <section
          className="category-details-section section-animate"
          ref={detailsRef}
        >
          <div className="section-content">
            <div className="category-details-container">
              <div className="category-details-info">
                <span className="section-subtitle">
                  {currentCategory.title.toUpperCase()}
                </span>
                <h2 className="section-title">{currentCategory.title}</h2>
                <p className="category-details-description">
                  {currentCategory.description}
                </p>

                <div className="category-features-modern">
                  <h3>Caratteristiche Principali</h3>
                  <ul className="features-list-modern">
                    {currentCategory.features.map((feature, index) => (
                      <li key={index}>
                        <span className="feature-check">✓</span>
                        {feature}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>

              <div className="category-details-visual">
                <Slideshow
                  images={categoryImages}
                  category={activeCategory}
                  loading={imagesLoading}
                />
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Info Box */}
      <section className="prodotti-footer-section">
        <div className="section-content">
          <InfoBox />
        </div>
      </section>
    </div>
  );
};

export default Prodotti;
